import { isTrialActive, trialAllowsAi } from "./trial.util";

/** Plan every organization member works under (matches API OrganizationPlans.Default). */
export const ORGANIZATION_PLAN_DEFAULT = "enterprise";

/**
 * While the account type is an organization, all members (admins, team admins, authors,
 * workers…) share the organization's subscription instead of their personal plan.
 */
export function getActiveOrganizationPlanId(): string | null {
  try {
    if (localStorage.getItem("accountUsageMode") !== "organization") return null;
    if (!localStorage.getItem("activeOrganizationId")) return null;
    const plan = (localStorage.getItem("activeOrganizationPlanId") || "").trim().toLowerCase();
    return plan || ORGANIZATION_PLAN_DEFAULT;
  } catch {
    return null;
  }
}

export function isOrganizationAccountMode(): boolean {
  return getActiveOrganizationPlanId() !== null;
}

export function getEffectivePlanId(user?: any): string {
  return getAssignedPlanId(user) || "standard";
}

/** Returns the assigned plan id, or null when the user has no plan. Never invents "standard". */
export function getAssignedPlanId(user?: any): string | null {
  if (!user) {
    return null;
  }
  return getActiveOrganizationPlanId() || getPersonalPlanId(user);
}

/** The user's own subscription, ignoring any organization they are working in. */
export function getPersonalPlanId(user?: any): string | null {
  if (!user) {
    return null;
  }
  const plan = String(user.effectivePlanId || user.EffectivePlanId || "")
    .trim()
    .toLowerCase();
  if (plan && plan !== "standard" && plan !== "none") {
    return plan;
  }
  if (user.userType === "Content Creator") {
    return "creator";
  }
  return null;
}

export function isCreatorPlanUser(user?: any): boolean {
  return getAssignedPlanId(user) === "creator";
}

export function isSuperAdminUser(user?: any): boolean {
  return user?.userRole === "Super Admin";
}

const CONTENT_PLANS = new Set(["creator", "starter", "growth", "enterprise"]);
/** Business plans that may create an organization the first time. */
const BUSINESS_ORG_PLANS = new Set(["starter", "growth", "enterprise"]);
/** Plans that include a monthly AI coin allowance (Creator does not). */
const AI_COIN_PLANS = new Set(["starter", "growth", "enterprise"]);

/** True only when a Creator/Starter/Growth/Enterprise plan is actually assigned. */
export function hasAssignedContentPlan(user?: any): boolean {
  const plan = getAssignedPlanId(user);
  return !!plan && CONTENT_PLANS.has(plan);
}

/** Creator, Starter, Growth, or Enterprise — required to create flows/bundles. */
export function hasContentCreationPlanAccess(user?: any): boolean {
  if (isSuperAdminUser(user)) {
    return true;
  }
  return hasAssignedContentPlan(user);
}

/**
 * Business role or Starter/Growth/Enterprise plan — required to create a first organization.
 * Site Admin / Super Admin always allowed.
 */
export function hasBusinessOrganizationAccess(user?: any): boolean {
  if (!user) return false;
  const role = String(user.userRole ?? user.UserRole ?? "").trim();
  if (role === "Super Admin" || role === "Admin") return true;

  const userType = String(user.userType ?? user.UserType ?? "").trim();
  const effectiveRole =
    !role || role === "User" ? userType || role : role;
  const normalized =
    effectiveRole === "Business Creator" ? "Business" : effectiveRole;
  // Organizations unlock once the Starter trial converts to paid (API enforces the same).
  if (isTrialActive()) return false;
  if (normalized === "Business") return true;

  const plan = getPersonalPlanId(user);
  return !!plan && BUSINESS_ORG_PLANS.has(plan);
}

/** Plans that include advanced flow access controls (IP, geo, device, company email). */
const ADVANCED_ACCESS_PLANS = new Set(["growth", "enterprise"]);

/** Growth or Enterprise — required for advanced access controls on a flow. */
export function hasAdvancedAccessControlPlan(user?: any): boolean {
  if (isSuperAdminUser(user)) {
    return true;
  }
  const plan = getAssignedPlanId(user);
  return !!plan && ADVANCED_ACCESS_PLANS.has(plan);
}

/** Plans that include a monthly AI coin allowance (coins are granted to the personal account). */
export function hasAiCoinPlanAccess(user?: any): boolean {
  if (isTrialActive()) return false;
  const plan = getPersonalPlanId(user);
  return !!plan && AI_COIN_PLANS.has(plan);
}

/** AI creation (Smart Create, Flowchest AI import, rephrase, keywords) is off during the Starter trial. */
export function hasAiCreationAccess(user?: any): boolean {
  if (isSuperAdminUser(user)) {
    return true;
  }
  return trialAllowsAi();
}

/** Show paid-feature badge on Smart Create when user has no monthly AI coin plan.
 * Creator plan does not include monthly AI coins, so the badge still shows.
 * Super Admin also sees the badge when no AI-coin plan is assigned (for QA / product clarity).
 */
export function showSmartCreatePaidBadge(user?: any): boolean {
  return !hasAiCoinPlanAccess(user);
}

export function getLoggedInUser(): any {
  try {
    return JSON.parse(localStorage.getItem("LoggedInUser") || "null");
  } catch {
    return null;
  }
}

export function updateLoggedInUser(updates: Record<string, unknown>): void {
  const user = getLoggedInUser();
  if (!user) {
    return;
  }
  Object.assign(user, updates);
  localStorage.setItem("LoggedInUser", JSON.stringify(user));
}
